import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkle, StackPlus, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";

export const DraftOptimizer = ({ clientId, onSavePreset }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [saved, setSaved] = useState({});

  const run = async () => {
    if (!clientId) { toast.error("Select a client first."); return; }
    setBusy(true); setSuggestions(null); setSaved({});
    try {
      const r = await api.draftingSuggestions(clientId);
      setSuggestions(r.suggestions || []);
      if (!r.suggestions?.length) toast.info("No suggestions returned — try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestions.");
    } finally { setBusy(false); }
  };

  const save = (s, i) => {
    onSavePreset?.({ title: s.title, blurb: s.blurb, prompt: s.prompt });
    setSaved((m) => ({ ...m, [i]: true }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && clientId && !suggestions && !busy) run(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="draft-optimizer-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--primary)] hover:bg-[var(--primary)]/10">
          <Sparkle size={14} weight="bold" /> Optimize
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px] max-h-[88vh] overflow-y-auto" data-testid="draft-optimizer-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Sparkle size={18} weight="bold" className="text-[var(--primary)]" />Drafting Optimization Suggestions</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">
            LispForge analyzes this client's standard profile &amp; recent routines and proposes high-value new automations. Save any as a one-click custom preset.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end">
          <Button size="sm" onClick={run} disabled={busy} data-testid="draft-optimizer-refresh"
            className="h-8 rounded-[3px] text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white gap-1.5">
            <ArrowClockwise size={13} weight="bold" /> {busy ? "Analyzing…" : (suggestions ? "Regenerate" : "Analyze")}
          </Button>
        </div>

        {busy && !suggestions && (
          <div className="text-center text-[var(--text-dim)] text-xs font-mono py-8" data-testid="draft-optimizer-loading">
            Reading the client's standards &amp; history…
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {(suggestions || []).map((s, i) => (
            <div key={i} className="border border-[var(--border)] rounded-[3px] p-3" data-testid={`draft-suggestion-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-mono text-[var(--text)]">{s.title}</div>
                  {s.blurb && <div className="text-[11px] text-[var(--text-dim)] mt-1 leading-snug">{s.blurb}</div>}
                </div>
                {saved[i] ? (
                  <span className="shrink-0 flex items-center gap-1 text-[11px] font-mono text-[var(--success)]"><CheckCircle size={13} weight="fill" /> Saved</span>
                ) : (
                  <button data-testid={`draft-suggestion-save-${i}`} onClick={() => save(s, i)}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-mono text-[var(--primary)] hover:underline">
                    <StackPlus size={13} weight="bold" /> Save as preset
                  </button>
                )}
              </div>
              <details className="mt-2">
                <summary className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] cursor-pointer hover:text-[var(--primary)]">View prompt</summary>
                <div className="text-[11px] font-mono text-[var(--text-dim)] whitespace-pre-wrap mt-1.5 leading-snug">{s.prompt}</div>
              </details>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
